import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {isNotNil, toBoolean} from '../core/convert';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedOverlayPositionChange,
  ConnectionPositionPair
} from '@angular/cdk/overlay';
import {BehaviorSubject, Observable} from 'rxjs';
import {animate, state, style, transition, trigger} from '@angular/animations';

function arrayMap<T, S>(array: T[], iteratee: (item: T, index: number, arr: T[]) => S): S[] {
  let index = -1;
  const length = array == null ? 0 : array.length;
  const result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}

function baseValues<T>(object: { [key: string]: T } | T[], props: string[]): T[] {
  return arrayMap(props, (key) => {
    return object[key];
  });
}

function _objectValues<T>(object: { [key: string]: T } | T[]): T[] {
  return object == null ? [] : baseValues(object, Object.keys(object));
}

export const POSITION_MAP: { [key: string]: ConnectionPositionPair } = {
  'top': {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom'
  },
  'topCenter': {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom'
  },
  'topLeft': {
    originX: 'start',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'bottom'
  },
  'topRight': {
    originX: 'end',
    originY: 'top',
    overlayX: 'end',
    overlayY: 'bottom'
  },
  'right': {
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center'
  },
  'rightTop': {
    originX: 'end',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'top'
  },
  'rightBottom': {
    originX: 'end',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'bottom'
  },
  'bottom': {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top'
  },
  'bottomCenter': {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top'
  },
  'bottomLeft': {
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top'
  },
  'bottomRight': {
    originX: 'end',
    originY: 'bottom',
    overlayX: 'end',
    overlayY: 'top'
  },
  'left': {
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center'
  },
  'leftTop': {
    originX: 'start',
    originY: 'top',
    overlayX: 'end',
    overlayY: 'top'
  },
  'leftBottom': {
    originX: 'start',
    originY: 'bottom',
    overlayX: 'end',
    overlayY: 'bottom'
  }
} as {} as { [key: string]: ConnectionPositionPair };

export const DEFAULT_4_POSITIONS = _objectValues([POSITION_MAP.top, POSITION_MAP.right, POSITION_MAP.bottom, POSITION_MAP.left]);

@Component({
  selector: 'jd-tool-tip',
  template: `
    <ng-content></ng-content>
    <ng-template
      #overlay="cdkConnectedOverlay"
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="overlayOrigin"
      [cdkConnectedOverlayHasBackdrop]="_hasBackdrop"
      (backdropClick)="hide()"
      (detach)="hide()"
      (positionChange)="onPositionChange($event)"
      [cdkConnectedOverlayPositions]="_positions"
      [cdkConnectedOverlayOpen]="visible$ | async">
      <div class="ant-tooltip" [ngClass]="_classMap" [ngStyle]="jdOverlayStyle" [@fadeAnimation]="''+(visible$ | async)"
           (@fadeAnimation.done)="_afterVisibilityAnimation($event)">
        <div class="ant-tooltip-content" *ngIf="jdTitle">
          <div class="ant-tooltip-arrow"></div>
          <div class="ant-tooltip-inner">
            <ng-container *ngIf="isTitleString; else titleTemplate">{{ jdTitle }}</ng-container>
            <ng-template #titleTemplate>
              <ng-template [ngTemplateOutlet]="jdTitle"></ng-template>
            </ng-template>
          </div>
        </div>
      </div>
    </ng-template>
  `,
  styles             : [ `
    .ant-tooltip {
      position: relative;
    }
  ` ],
  animations: [
    trigger('fadeAnimation', [
      state('void', style({ opacity: 0 })),
      state('true', style({ opacity: 1 })),
      state('false', style({ opacity: 0 })),
      transition('* => true', animate('150ms cubic-bezier(0.0, 0.0, 0.2, 1)')),
      transition('* => void', animate('150ms cubic-bezier(0.4, 0.0, 1, 1)')),
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JdToolTipComponent implements OnInit {
  _hasBackdrop = false;
  _prefix = 'ant-tooltip-placement';
  _positions: ConnectionPositionPair[] = [...DEFAULT_4_POSITIONS];
  _classMap = {};
  _placement = 'top';
  _trigger = 'hover';
  _content: string | TemplateRef<void>;
  overlayOrigin: CdkOverlayOrigin;
  isContentString: boolean;
  isTitleString: boolean;
  visibleSource = new BehaviorSubject<boolean>(false);
  visible$: Observable<boolean> = this.visibleSource.asObservable();
  @ContentChild('jdTemplate') _title: string | TemplateRef<void>;
  @ViewChild('overlay') overlay: CdkConnectedOverlay;
  @Output() jdVisibleChange: EventEmitter<boolean> = new EventEmitter();

  @Input() jdOverlayClassName = '';
  @Input() jdOverlayStyle: { [key: string]: string } = {};
  @Input() jdMouseEnterDelay = 0.15; // Unit: second
  @Input() jdMouseLeaveDelay = 0.1; // Unit: second
  @Input()
  set jdContent(value: string | TemplateRef<void>) {
    this.isContentString = !(value instanceof TemplateRef);
    this._content = value;
  }

  get jdContent(): string | TemplateRef<void> {
    return this._content;
  }

  @Input()
  set jdTitle(value: string | TemplateRef<void>) {
    this.isTitleString = !(value instanceof TemplateRef);
    this._title = value;
  }

  get jdTitle(): string | TemplateRef<void> {
    return this._title;
  }

  @Input()
  set jdVisible(value: boolean) {
    const visible = toBoolean(value);
    if (this.visibleSource.value !== visible) {
      this.visibleSource.next(visible);
      this.jdVisibleChange.emit(visible);
    }
  }

  get jdVisible(): boolean {
    return this.visibleSource.value;
  }

  @Input()
  set jdTrigger(value: string) {
    this._trigger = value;
    this._hasBackdrop = this._trigger === 'click';
  }

  get jdTrigger(): string {
    return this._trigger;
  }

  @Input()
  set jdPlacement(value: string) {
    if (value !== this._placement) {
      this._placement = value;
      this._positions.unshift(POSITION_MAP[this.jdPlacement] as ConnectionPositionPair);
    }
  }

  get jdPlacement(): string {
    return this._placement;
  }

  // Manually force updating current overlay's position
  updatePosition(): void {
    if (this.overlay && this.overlay.overlayRef) {
      this.overlay.overlayRef.updatePosition();
    }
  }

  onPositionChange($event: ConnectedOverlayPositionChange): void {
    for (const key in POSITION_MAP) {
      if (JSON.stringify($event.connectionPair) === JSON.stringify(POSITION_MAP[key])) {
        this.jdPlacement = key;
        break;
      }
    }
    this.setClassMap();
    /** TODO may cause performance problem */
    this.cdr.detectChanges();
  }

  show(): void {
    if (!this.isContentEmpty()) {
      this.jdVisible = true;
    }
  }

  hide(): void {
    this.jdVisible = false;
  }

  _afterVisibilityAnimation(e: any): void {
    if (e.toState === 'false' && !this.jdVisible) {
      this.jdVisibleChange.emit(false);
    }
    if (e.toState === 'true' && this.jdVisible) {
      this.jdVisibleChange.emit(true);
    }
  }

  setClassMap(): void {
    this._classMap = {
      [this.jdOverlayClassName]: true,
      [`${this._prefix}-${this._placement}`]: true
    };
  }

  setOverlayOrigin(origin: CdkOverlayOrigin): void {
    this.overlayOrigin = origin;
  }

  constructor(public cdr: ChangeDetectorRef) {
  }

  isContentEmpty(): boolean {
    return this.isTitleString ? (this.jdTitle === '' || !isNotNil(this.jdTitle)) : false;
  }

  ngOnInit() {
  }
}
